import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import api from '../../utils/api';
import { deleteAllAssets, setClean, setInteractiveData, setItem } from '../../actions';
import UuidContext from '../../contexts/UuidContext';
import LockContext from '../../contexts/LockContext';

import { ConfirmNavigation, Frame, InteractiveToolbar, Nav } from '.';
import { Notification } from '../Global';
import { SplitScreen } from './SplitScreen';
import { LockedDialog } from './LockedDialog';
import LastEditedContext from '../../contexts/LastEditedContext';

export const InteractiveWrapper = React.memo(({children,hasStyles,interactive,name,title,template,initFunction, ...props}) => {
  const { id } = useParams();
  const [given, setGiven] = useState({})
  const [loaded, setLoaded] = useState(!id)
  const history = useHistory();
  const dispatch = useDispatch();

  const userEmail = useSelector(state => state.user.email)

  /** 
   * @todo
   * uuid, lock, and lastEdited are typically used in the same places.
   * Would likely be better off in a reducer together.
   */
  const [uuid, setUuid] = useState('');
  const uuidContextValue = { uuid, setUuid };

  const [lock, setLock] = useState('');
  const lockContextValue = { lock, setLock };
  const lockIdRef = useRef(lock.id)

  const [lastEdited, setLastEdited] = useState('');
  const lastEditedContextValue = { lastEdited, setLastEdited };

  const [notification, setNotification] = useState({
    message: '',
    open: false,
    severity: ''
  });


  // Keeps value of lockIdRef up-to-date
  useEffect(() => {
    lockIdRef.current = lock.id
  }, [lock.id]); 


  // Load the interactive if we have an id
  useEffect(() => {
    if (id) {
      // Set document title
      document.title = `Edit ${title} | PAVILION Interactive Builder`

      // Load interactive data
      api.getInteractive(id)
        .then(res => {
          const { data } = res
          const given = JSON.parse(data.interactiveData);
          setGiven(given)
          setUuid(given.uuid)
          dispatch(setItem('uuid', given.uuid))
          setLoaded(true); // need to do before redirect
          setLastEdited(data.lastEdited)

          // If locked, display modal
          if (data.lockOwnerEmail) {
            setLock({
              id: data.lockOwnerEmail === userEmail ? data.lockUuid : '',
              ownerEmail: data.lockOwnerEmail,
              owner: data.lockOwner,
            })
          }
          
          // If id doesn't match the interactive type we are on, 
          // redirect to the correct type
          if (data.type !== name) {
            history.push(`/${data.type}/${id}`, {dirty:false})
          }
        })
        .catch(err => {
          setNotification({
            open: true,
            severity: 'error',
            message: 'Item not found.',
          })
          setLoaded(true); // need to do before redirect

          // If there was an error getting an interactive with this id,
          // redirect to create a new interactive of this type
          history.push(`/${name}`, {dirty:false})
        })
    } else {
      // Set document title
      document.title = `Create ${title} | PAVILION Interactive Builder`
    }
  }, [dispatch,history,id,name,setNotification,setUuid,title,userEmail])


  const Template = template;
  const Form = React.cloneElement(children, {given});

  const Preview = template ?
                    <Frame
                      topPadding={props.topPadding}
                      initFunction={initFunction}
                      name={name}
                      hasStyles={hasStyles}
                    >
                      <Template {...interactive} />
                    </Frame>
                  : 
                    <div>No template found</div>

  // set new uuid when creating new interactive
  useEffect(() => {
    if (!uuid && loaded) {
      const newUuid = uuidv4();
      dispatch(setItem('uuid',newUuid));
      setUuid(newUuid)
    }
  },[dispatch,loaded,setUuid,uuid])
                  
  // Reset interactive data in store when component unmounts
  useEffect(() => () => {
    dispatch(setInteractiveData({}))
    dispatch(deleteAllAssets())
    dispatch(setClean())
  }, [dispatch])

  // Unlock when component unmounts
  useEffect(() => () => {
    setLock({hide:true})

    // Using ref allows us to distinguish unmount from remount
    if (lockIdRef.current) {
      api.unlockIntv(lockIdRef.current)
    }
  }, [])


  return (
    <UuidContext.Provider value={uuidContextValue}>
      <LockContext.Provider value={lockContextValue}>
        <LastEditedContext.Provider value={lastEditedContextValue}>

          {/* Modal that displays when navigating away from unsaved work */}
          <ConfirmNavigation />

          {/* Modal that displays when interactive is locked */}
          <LockedDialog setNotification={setNotification} />

          {/* Main nav */}
          <Nav dense={true} />

          {/* Secondary nav */}
          <InteractiveToolbar 
            title={title}
            name={name}
            template={template}
            setNotification={setNotification}
          />

          {loaded 
          ? <SplitScreen
              dense={true}
              leftSide={Form}
              rightSide={Preview}
            />
          : '' }

          {/* Displays error/success notifications */}
          <Notification {...notification} setNotification={setNotification} />

          
        </LastEditedContext.Provider>
      </LockContext.Provider>
    </UuidContext.Provider> 
  )
})